import _ from 'lodash'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useAppConfig } from '../../hooks/useAppConfig'
import { PersistValueKeys, usePersistedState } from '../../hooks/usePersistedState'
import { Badge } from '../Badge'
import { Box } from '../Box'
import { Button } from '../Button'
import { Dropdown } from '../Dropdown'
import { Flex, FlexProps } from '../Flex'
import { Image } from '../Image'
import { Menu, MenuItem } from '../Menu'
import { Text } from '../Text'
import { Color, themeColor, themeSpace, ThemeWrapper } from '../theme'
import { Icon } from '../Icons'
import { useViewportSize } from '../../hooks'

import { FlagIcon, flagMap, FlagMapKeys } from './Flags'
import { NotificationCounter } from './NotificationCounter'

const StyledHeader = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  background: ${({ theme }) => theme.colors.white};
  height: 72px;
  padding: 0 ${themeSpace(5)};
`

const Brand = styled(Image)`
  display: block;
  max-height: 45px;
`

const IconsContainer = styled(Flex)`
  align-items: center;
  & > .anticon {
    margin-left: ${({ theme }) => theme.space[2]};
    font-size: 1.5em;
  }

  button {
    padding: 0 16px;
  }
`

const ProfileButtons = styled.div<{ open: boolean }>`
  display: flex;
  flex-shrink: 10;
  overflow: hidden;
  max-width: ${({ open }) => (open ? '100%' : '0')};
  transition: max-width 250ms ease-out;
`

type ProfileButtonProps = {
  wide?: boolean
  hoverBackgroundColor?: Color
  active?: boolean
  color?: Color
} & FlexProps

const ProfileButton = styled(Flex)<ProfileButtonProps>`
  justify-content: center;
  align-items: center;
  font-weight: bold;
  cursor: pointer;

  padding-left: ${({ wide }) => (wide ? 48 : 23)}px;
  padding-right: ${({ wide }) => (wide ? 48 : 23)}px;
`

const TransparentProfileButton = styled(ProfileButton)`
  color: ${themeColor('primary')};
`

const UserNameButton = styled(TransparentProfileButton)`
  flex-shrink: 10;
`

const PrimaryDarkProfileButton = styled(ProfileButton)`
  color: ${({ active }) => themeColor(active ? 'secondary' : 'white')};
  background: ${themeColor('primaryDark')};
  position: relative;
  &:hover {
    background: ${themeColor('primary')};
  }

  &::before {
    content: ' ';
    position: absolute;
    top: 20%;
    left: calc(50% - 3px);
    display: ${({ active }) => (active ? 'block' : 'none')};
    background: ${themeColor('secondary')};
    width: 6px;
    height: 6px;
    transform: rotate(45deg);
    border-radius: 1px;
  }
`

const PrimaryProfileButton = styled(ProfileButton)`
  background: ${themeColor('primary')};
  &:hover {
    background: ${themeColor('primaryLight')};
  }
`

const LanguageMenuItem = styled(MenuItem)`
  &:hover {
    background: ${themeColor('primaryLightest')} !important;
  }
`

const AiStyledButton = styled(Button)`
  padding-left: ${({ theme }) => theme.space[1]} !important;
  padding-right: ${({ theme }) => theme.space[1]} !important;
`

const AiStyledIcon = styled(Icon)`
  overflow: hidden;
  border-radius: 4px;
`

const AiHorizontalButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: ${({ theme }) => theme.space[3]};
  width: 140px;
  height: 48px;
  min-width: 140px;
  overflow: hidden;
  border-radius: 4px;
  cursor: pointer;
`

const FullNameBox = styled(Box)`
  max-height: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`

const HeaderMenu = styled(Flex)`
  max-height: 100%;
`

export type RouteButtons = 'Archive' | 'Profile' | 'BulkImport'

export interface HeaderProps extends React.PropsWithRef<FlexProps> {
  homeUrl: string
  changeLanguage: (lng: string) => void
  logout: () => void
  activeButton: RouteButtons | undefined
  cmsFaqUrl: string | undefined
  loggedIn: boolean
  fullName: string
  logoUrl: {
    url: string
    alt?: string
  }
  countOfOrderItems: number
  showBulkImportMenuInHeader: boolean | undefined
  loginUrl: string
  links: {
    Archive: React.FunctionComponent
    Profile: React.FunctionComponent
    Bulk: React.FunctionComponent
    Cart: React.FunctionComponent
    Notifications: React.FunctionComponent
  }
  countUnreadNotifications: number
  handleMarkAllNotificationsReadClick: () => void
  showAiChatbotButton: boolean
  toggleAiChatbotWidget: () => void
}

export const Header = (props: HeaderProps) => {
  const {
    homeUrl,
    changeLanguage,
    logout,
    activeButton,
    cmsFaqUrl,
    loggedIn,
    fullName,
    logoUrl,
    countOfOrderItems,
    showBulkImportMenuInHeader,
    loginUrl,
    links,
    countUnreadNotifications,
    handleMarkAllNotificationsReadClick,
    showAiChatbotButton,
    toggleAiChatbotWidget
  } = props
  const {
    Archive: ArchiveLink,
    Bulk: BulkLink,
    Cart: CartLink,
    Notifications: NotificationsLink,
    Profile: ProfileLink
  } = links
  const { availableLanguages } = useAppConfig()
  const { t, i18n } = useTranslation(['core', 'archive', 'account', 'import'])
  const [profileOpen, setProfileOpen] = usePersistedState(PersistValueKeys.UserMenuExpanded, true)
  const { $isSmallDesktop } = useViewportSize() ?? {}

  const languageMenu = (
    <Menu>
      {_.map(availableLanguages, l => (
        <LanguageMenuItem key={l}>
          <Button
            variant="transparent"
            onClick={() => {
              changeLanguage(l)
            }}
            icon={<FlagIcon active={i18n.language === l} type={flagMap[l as FlagMapKeys]} />}
            iconPlacement="left"
          >
            {_.toUpper(l)}
          </Button>
        </LanguageMenuItem>
      ))}
    </Menu>
  )

  return (
    <ThemeWrapper>
      <StyledHeader {...props}>
        <a href={homeUrl}>
          <Brand src={logoUrl.url} alt={logoUrl.alt} />
        </a>

        <HeaderMenu justifyContent="flex-end">
          {loggedIn && (
            <ProfileButtons open={profileOpen} data-automation-id="menuProfileButtons">
              <UserNameButton onClick={() => setProfileOpen(false)}>
                <Image type="userCheck" data-automation-id="menuUserCheck" alt="user check" />
                <FullNameBox ml={2} data-automation-id="menuUserName">
                  {fullName}
                </FullNameBox>
              </UserNameButton>
              <ArchiveLink data-automation-id="menuArchiveLink">
                <PrimaryDarkProfileButton active={activeButton === 'Archive'} wide={!$isSmallDesktop}>
                  {t('archive:Archive')}
                </PrimaryDarkProfileButton>
              </ArchiveLink>
              <ProfileLink data-automation-id="menuProfileLink">
                <PrimaryDarkProfileButton active={activeButton === 'Profile'} wide={!$isSmallDesktop}>
                  {t(`account:Menu.Profile`)}
                </PrimaryDarkProfileButton>
              </ProfileLink>
              {showBulkImportMenuInHeader && (
                <BulkLink>
                  <PrimaryDarkProfileButton active={activeButton === 'BulkImport'} wide={!$isSmallDesktop}>
                    {t(`import:BulkImport`)}
                  </PrimaryDarkProfileButton>
                </BulkLink>
              )}
              <PrimaryProfileButton
                backgroundColor="primary"
                hoverBackgroundColor="primaryLight"
                onClick={logout}
                data-automation-id="menuLogOut"
              >
                <Image type="logOutWhite" className="qa--logout-button-image" alt="log out" />
              </PrimaryProfileButton>
            </ProfileButtons>
          )}

          <IconsContainer py={2}>
            {showAiChatbotButton &&
              ($isSmallDesktop ? (
                <AiStyledButton
                  onClick={() => {
                    toggleAiChatbotWidget()
                  }}
                  icon={<Icon type="AiAgent" />}
                  variant="primary"
                  iconPlacement="left"
                  ml={3}
                />
              ) : (
                <AiHorizontalButton>
                  <AiStyledIcon
                    type="AiAgentHorizontal"
                    onClick={() => {
                      toggleAiChatbotWidget()
                    }}
                  />
                </AiHorizontalButton>
              ))}
            {!profileOpen && loggedIn && (
              <Button variant="transparent" onClick={() => setProfileOpen(!profileOpen)}>
                <Image type="user" alt="user" />
              </Button>
            )}
            {!loggedIn && (
              <a href={loginUrl}>
                <Button variant="transparent" data-automation-id="menuLogOut">
                  <Image src="userLoggedOut" alt="user logged out" />
                </Button>
              </a>
            )}
            {loggedIn && (
              <NotificationsLink data-automation-id="menuNotificationsLink">
                <NotificationCounter
                  countUnreadNotifications={countUnreadNotifications}
                  handleMarkAllReadClick={handleMarkAllNotificationsReadClick}
                  data-automation-id="menuNotificationsCounter"
                />
              </NotificationsLink>
            )}
            <CartLink>
              <Button variant="transparent" overflow="visible !important" data-automation-id="menuShoppingCart">
                <Badge count={countOfOrderItems} offset={[3, -3]}>
                  <Image type="shoppingCart" alt="shopping cart" />
                </Badge>
              </Button>
            </CartLink>
            {cmsFaqUrl && (
              <a href={cmsFaqUrl}>
                <Button variant="transparent" data-automation-id="menuQuestion">
                  <Image type="question" alt="question" />
                </Button>
              </a>
            )}
            {_.size(availableLanguages) > 1 && (
              <Box pr={1} display="flex">
                <Dropdown dropdownRender={() => languageMenu} trigger={['hover', 'click']}>
                  <Button variant="transparent" icon={<Image type="triangleDown" alt="triangle down" />}>
                    <Text color="primary" fontWeight="bold">
                      {_.toUpper(i18n.language)}
                    </Text>
                  </Button>
                </Dropdown>
              </Box>
            )}
          </IconsContainer>
        </HeaderMenu>
      </StyledHeader>
    </ThemeWrapper>
  )
}
